class ProductCompare extends HTMLElement {
  constructor() {
    super();
    this.compareButton = this.querySelector('.product__link--scale');

    if (this.compareButton) {
      this.setupEventListeners();
      this.updateButtonState();
    }
  }

  setupEventListeners() {
    this.compareButton.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleCompare();
    });

    window.addEventListener('compareListCleared', () => {
      this.updateButtonState();
    });

    window.addEventListener('compareListUpdated', () => {
      this.updateButtonState();
    });

    document.addEventListener('variant:updated', (e) => {
      setTimeout(() => {
        this.syncCompareListWithCurrentVariant();
        this.updateButtonState();
      }, 0);
    });
  }

  toggleCompare() {
    const compareList = JSON.parse(localStorage.getItem('productComparison') || '[]');
    const productData = this.getProductInfo();

    if (!productData) return;

    const productIndex = compareList.findIndex((item) => item.variantId === productData.variantId);

    if (productIndex === -1) {
      compareList.push(productData);

      setTimeout(() => {
        const compareNotification = document.querySelector('compare-notification');
        if (compareNotification && typeof compareNotification.open === 'function') {
          compareNotification.open();
        }
      }, 100);
    } else {
      compareList.splice(productIndex, 1);
    }

    localStorage.setItem('productComparison', JSON.stringify(compareList));
    this.updateButtonState();

    window.dispatchEvent(new CustomEvent('compareListUpdated'));
  }

  updateButtonState() {
    if (!this.compareButton) {
      return;
    }

    const compareList = JSON.parse(localStorage.getItem('productComparison') || '[]');
    const productData = this.getProductInfo();

    if (!productData || !productData.variantId) {
      this.compareButton.classList.remove('is-active');
      const textSpan = this.compareButton.querySelector('span');
      if (textSpan) {
        textSpan.textContent = window.theme.strings.product_comparison.compare_add;
      }
      return;
    }

    const currentVariantId = String(productData.variantId);
    const isInCompare = compareList.some((item) => String(item.variantId) === currentVariantId);

    this.compareButton.classList.toggle('is-active', isInCompare);
    const textSpan = this.compareButton.querySelector('span');
    if (textSpan) {
      textSpan.textContent = isInCompare
        ? window.theme.strings.product_comparison.compare_remove
        : window.theme.strings.product_comparison.compare_add;
    }
  }

  decodeHtmlEntities(text) {
    if (!text) return '';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  deriveProductHandleFromUrl(url) {
    if (!url || typeof url !== 'string') return '';
    try {
      const pathOnly = url.startsWith('http') ? new URL(url).pathname : url.split('?')[0];
      const m = pathOnly.match(/\/products\/([^/?#]+)/);
      return m ? decodeURIComponent(m[1]) : '';
    } catch (e) {
      return '';
    }
  }

  syncCompareListWithCurrentVariant() {
    const productData = this.getProductInfo();
    if (!productData?.variantId) return;

    const compareList = JSON.parse(localStorage.getItem('productComparison') || '[]');
    const index = compareList.findIndex((item) => String(item.variantId) === String(productData.variantId));
    if (index === -1) return;

    compareList[index] = { ...compareList[index], ...productData };
    localStorage.setItem('productComparison', JSON.stringify(compareList));
    window.dispatchEvent(new CustomEvent('compareListUpdated'));
  }

  getProductInfo() {
    const infoDiv = this.querySelector('.product-information');
    if (!infoDiv) return null;

    const variantId = infoDiv.dataset.variantId;
    if (!variantId) return null;

    const productHandle =
      (infoDiv.dataset.productHandle || '').trim() || this.deriveProductHandleFromUrl(infoDiv.dataset.productUrl);

    const base = {
      id: variantId,
      productId: infoDiv.dataset.productId,
      variantId: variantId,
      title: infoDiv.dataset.productTitle,
      price: infoDiv.dataset.productPrice,
      priceWithVat: infoDiv.dataset.productPriceWithVat,
      compareAtPrice: infoDiv.dataset.productCompareAtPrice || '',
      compareAtPriceWithVat: infoDiv.dataset.productCompareAtPriceWithVat || '',
      availability: infoDiv.dataset.productAvailability,
      services: infoDiv.dataset.productServices,
      engine: infoDiv.dataset.carEngine,
      transmission: infoDiv.dataset.carTransmission,
      fuel: infoDiv.dataset.productFuel,
      power: infoDiv.dataset.productPower,
      engineVolume: infoDiv.dataset.carEngineVolume,
      combinedConsumption: infoDiv.dataset.carCombinedConsumption,
      maxSpeed: infoDiv.dataset.carMaxSpeed,
      luggageVolume: infoDiv.dataset.carLuggageVolume,
      electricFuelCombinedConsumption: infoDiv.dataset.carElectricFuelCombinedConsumption,
      batteryCapacity: infoDiv.dataset.carBatteryCapacity,
      range: infoDiv.dataset.carRange,
      driveType: infoDiv.dataset.productDriveType,
      equipmentFilter: infoDiv.dataset.carEquipmentFilter,
      equipment: this.decodeHtmlEntities(infoDiv.dataset.carEquipment || ''),
      image: infoDiv.dataset.productImage,
      url: infoDiv.dataset.productUrl,
      color: infoDiv.dataset.productColor,
      rentalPeriod: infoDiv.dataset.productRentalPeriod,
      annualMileage: infoDiv.dataset.productAnnualMileage,
    };
    return productHandle ? { ...base, productHandle } : base;
  }
}

customElements.define('product-compare', ProductCompare);

function formatMoney(valueInCents, locale = 'cs-CZ') {
  const value = valueInCents / 100;
  const currency = Shopify.currency.active;

  const fullFormatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

  let lastSpaceIndex = -1;
  for (let i = fullFormatted.length - 1; i >= 0; i--) {
    if (fullFormatted[i] === ' ' || fullFormatted[i] === '\u00A0') {
      lastSpaceIndex = i;
      break;
    }
  }

  if (lastSpaceIndex !== -1) {
    let numberPart = fullFormatted.substring(0, lastSpaceIndex);

    const currencyPart = fullFormatted.substring(lastSpaceIndex);

    numberPart = numberPart.replace(/[\s\u00A0]/g, '.');

    return numberPart + currencyPart;
  } else {
    return fullFormatted;
  }
}
